import uniqueId from "lodash/uniqueId";
import { useCustomValuePreviewActions } from "PFApp/components/custom_value_preview/use_custom_value_preview";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import {
  ActivityCustomField,
  ActivityPureCustomValue,
  CustomType,
  CustomValueScope,
  Experience,
  Id,
  Importance,
  Profile,
  PureCustomValueData,
  Template
} from "PFCore/types";
import { useEffect } from "react";
import { useNumber } from "react-use";

type UseMatchAttributeSidepanel = {
  value: ActivityPureCustomValue[];
  customType: CustomType;
  isMultiple: boolean;
  template: Template;
  activityCreator: Profile | null;
  connectedValuesIds: Id[];
  onChange: (newValue: PureCustomValueData[]) => void;
};

export const useMatchAttributeSidepanel = ({
  value,
  customType,
  isMultiple,
  template,
  activityCreator,
  connectedValuesIds,
  onChange
}: UseMatchAttributeSidepanel) => {
  const { showPreview } = useCustomValuePreviewActions();
  const { data: currentProfile } = useCurrentProfile();
  const [reopen, { inc: triggerReopen }] = useNumber(0);

  const handleSidePanelOpen = (customValue?: (typeof value)[0]) => {
    const onValueChange = (newCustomFields: ActivityCustomField[]) => {
      const newValue = newCustomFields[0].values;
      onChange(newValue);
    };
    const commonProps = {
      customTypeId: customType.id,
      scope: CustomValueScope.Activity as const,
      canEdit: true,
      staticActivity: {
        template_id: template.id,
        profile: activityCreator ?? currentProfile,
        custom_fields: [{ type: { id: Number(customType.id), name: customType.name }, values: value }]
      },
      staticActivityConnection: {
        importance: customValue?.importance ?? Importance.Essential,
        requiredExperience: customValue?.required_experience ?? Experience.Intermediate
      },
      connectedValuesIds,
      key: uniqueId(),
      onSave: onValueChange,
      onRemove: onValueChange,
      onReopen: isMultiple ? triggerReopen : undefined
    };

    if (!customValue) {
      showPreview({
        ...commonProps,
        isNew: true,
        allowSearch: true
      });
      return;
    }

    showPreview({
      ...commonProps,
      customValueId: customValue.id,
      isNew: false,
      allowSearch: false
    });
  };

  useEffect(() => {
    reopen && handleSidePanelOpen();
  }, [reopen]);

  return { handleSidePanelOpen };
};
